import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { CandidateCreateModal } from './CandidateCreateModal.jsx';
import { JobPostingCreateModal } from './JobPostingCreateModal.jsx';

const EMPTY = { jobPostingId: '', candidateId: '', notes: '' };

export function ApplicationCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [postingModalOpen, setPostingModalOpen] = useState(false);
  const [candidateModalOpen, setCandidateModalOpen] = useState(false);

  const postings = useQuery({
    queryKey: ['hr', 'job-postings'],
    queryFn: () => hrApi.listJobPostings().then((r) => r.items || []),
    enabled: open,
  });
  const candidates = useQuery({
    queryKey: ['hr', 'candidates'],
    queryFn: () => hrApi.listCandidates().then((r) => r.items || []),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: (body) => hrApi.submitApplication(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'applications'] });
      setForm(EMPTY);
      setError(null);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  const handle = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  function submit(e) {
    e.preventDefault();
    setError(null);
    create.mutate({
      jobPostingId: form.jobPostingId,
      candidateId: form.candidateId,
      notes: form.notes.trim() || undefined,
    });
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={t('rec.applications.modal.title')}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
            <Button variant="primary" onClick={submit} disabled={create.isPending}>
              {create.isPending ? `${t('common.save')}…` : t('common.create')}
            </Button>
          </>
        }
      >
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <label className="field">
            <span className="field-label">{t('rec.applications.modal.posting')}</span>
            <div className="inline-create">
              <select className="select" value={form.jobPostingId} onChange={handle('jobPostingId')} required>
                <option value="">{t('finance.invoices.modal.pickCounterparty')}</option>
                {(postings.data || []).map((p) => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
              <Button type="button" variant="ghost" className="inline-create-btn" onClick={() => setPostingModalOpen(true)}>
                + {t('common.new')}
              </Button>
            </div>
          </label>
          <label className="field">
            <span className="field-label">{t('rec.applications.modal.candidate')}</span>
            <div className="inline-create">
              <select className="select" value={form.candidateId} onChange={handle('candidateId')} required>
                <option value="">{t('finance.invoices.modal.pickCounterparty')}</option>
                {(candidates.data || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.firstName} {c.lastName} - {c.email}</option>
                ))}
              </select>
              <Button type="button" variant="ghost" className="inline-create-btn" onClick={() => setCandidateModalOpen(true)}>
                + {t('common.new')}
              </Button>
            </div>
          </label>
          <label className="field">
            <span className="field-label">{t('rec.applications.modal.notes')}</span>
            <textarea className="textarea" rows={2} value={form.notes} onChange={handle('notes')} />
          </label>
          {error && <div className="alert danger">{error}</div>}
        </form>
      </Modal>
      <JobPostingCreateModal
        open={postingModalOpen}
        onClose={() => setPostingModalOpen(false)}
        onCreated={(created) => {
          queryClient.invalidateQueries({ queryKey: ['hr', 'job-postings'] });
          if (created?.id) {
            setForm((p) => ({ ...p, jobPostingId: created.id }));
          }
        }}
      />
      <CandidateCreateModal
        open={candidateModalOpen}
        onClose={() => setCandidateModalOpen(false)}
        onCreated={(created) => {
          queryClient.invalidateQueries({ queryKey: ['hr', 'candidates'] });
          if (created?.id) {
            setForm((p) => ({ ...p, candidateId: created.id }));
          }
        }}
      />
    </>
  );
}
